import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { memo } from 'react';

import Heading from 'shopper/components/Heading';

import { sendEvent } from 'lib/gtag';
import { sliceWithEllipsis } from 'lib/string';

import CommentsList from '../CommentsList';
import Actions from './Actions';
import Authors from './Authors';
import CommentsCount from './CommentsCount';
import GoToStoreButton from './GoToStoreButton';
import FinishedOfferButtons from './GoToStoreButton/FinishedOfferButtons';
import Hero from './Hero';
import Price from './Price';
import PublishDetails from './PublishDetails';
import ReviewsCount from './ReviewsCount';

const Tags = dynamic(() => import('./Tags'));

const Main = ({
  authorCount,
  authors,
  gaEventCategory,
  isFinished,
  isOfferCommentsAllowed,
  keywords,
  mostRelevantKeyword,
  offerCta = null,
  offerCommentsCount,
  offerCoupon,
  offerId,
  offerInstructions,
  offerKey,
  offerLikes,
  offerOldPrice,
  offerPhoto,
  offerPrice,
  offerPriceType,
  offerStatusName,
  offerTags = [],
  offerTitle,
  offerUserVisibility,
  reviews,
  serverComments,
  storeDomain,
  storeImage,
  storeName,
  totalReviews,
  userId,
  userName,
  userSpecialtyName,
  userUsername,
}) => {
  const onOfferTitleClick = () => {
    sendEvent({ action: 'title', category: gaEventCategory });
  };

  return (
    <section className="flex flex-col gap-6 lg:max-h-[722px] lg:flex-row">
      <div className="flex flex-col gap-4 lg:w-[690px]">
        <Hero
          gaEventCategory={gaEventCategory}
          isFinished={isFinished}
          offerId={offerId}
          offerPhoto={offerPhoto}
          offerTitle={offerTitle}
        />
        <div className="grid gap-3">
          <div className="flex items-center gap-x-1">
            <ReviewsCount>{totalReviews}</ReviewsCount>
            <CommentsCount offerId={offerId}>
              {offerCommentsCount}
            </CommentsCount>
            <Actions
              gaEventCategory={gaEventCategory}
              keywords={keywords}
              offerCoupon={offerCoupon}
              offerId={offerId}
              offerInstructions={offerInstructions}
              offerLikes={offerLikes}
              offerPrice={offerPrice}
              offerPriceType={offerPriceType}
              offerStatusName={offerStatusName}
              offerTitle={offerTitle}
              userId={userId}
            />
          </div>
          <Heading
            as="h1"
            className="text-pretty"
            size="size4"
            onClick={onOfferTitleClick}
          >
            {sliceWithEllipsis(offerTitle, 180)}
          </Heading>
          {offerTags.length > 0 && <Tags tags={offerTags} />}
          <PublishDetails
            storeDomain={storeDomain}
            storeImage={storeImage}
            storeName={storeName}
          />
          <div className="flex flex-col gap-x-6 gap-y-2">
            <Price
              offerOldPrice={offerOldPrice}
              offerPrice={offerPrice}
              offerPriceType={offerPriceType}
              size="large"
            />
            {isFinished ? (
              <FinishedOfferButtons
                gaEventCategory={gaEventCategory}
                mostRelevantKeyword={mostRelevantKeyword}
                offerId={offerId}
                offerUserVisibility={offerUserVisibility}
              />
            ) : (
              <GoToStoreButton
                gaEventCategory={gaEventCategory}
                offerCoupon={offerCoupon}
                offerCta={offerCta}
                offerId={offerId}
                offerUserVisibility={offerUserVisibility}
              />
            )}
            <Authors
              authorCount={authorCount}
              authors={authors}
              offerId={offerId}
              userId={userId}
              userName={userName}
              userSpecialtyName={userSpecialtyName}
              userUsername={userUsername}
            />
          </div>
        </div>
      </div>
      <CommentsList
        className="lg:w-[550px]"
        gaEventCategory={gaEventCategory}
        isOfferCommentsAllowed={isOfferCommentsAllowed}
        offerCommentsCount={offerCommentsCount}
        offerId={offerId}
        offerKey={offerKey}
        offerStatusName={offerStatusName}
        reviews={reviews}
        serverComments={serverComments}
      />
    </section>
  );
};

Main.propTypes = {
  authorCount: PropTypes.number.isRequired,
  authors: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  gaEventCategory: PropTypes.string.isRequired,
  isFinished: PropTypes.bool.isRequired,
  keywords: PropTypes.arrayOf(PropTypes.string),
  mostRelevantKeyword: PropTypes.string.isRequired,
  offerCta: PropTypes.string,
  offerCommentsCount: PropTypes.number.isRequired,
  offerCoupon: PropTypes.string,
  offerId: PropTypes.number.isRequired,
  offerInstructions: PropTypes.string,
  offerIsHighlight: PropTypes.bool.isRequired,
  offerKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  offerLikes: PropTypes.number.isRequired,
  offerOldPrice: PropTypes.number,
  offerPhoto: PropTypes.string.isRequired,
  offerPrice: PropTypes.number.isRequired,
  offerPriceType: PropTypes.string.isRequired,
  offerStatusName: PropTypes.string.isRequired,
  offerTags: PropTypes.arrayOf(PropTypes.shape()),
  offerTitle: PropTypes.string.isRequired,
  offerUserVisibility: PropTypes.string.isRequired,
  serverComments: PropTypes.arrayOf(
    PropTypes.shape({
      commentId: PropTypes.number.isRequired,
      parentId: PropTypes.number,
      userId: PropTypes.number.isRequired,
      commentText: PropTypes.string.isRequired,
      commentRaw: PropTypes.string.isRequired,
      commentDate: PropTypes.string.isRequired,
      commentLikes: PropTypes.number.isRequired,
      commentStatusName: PropTypes.string.isRequired,
      commentFixed: PropTypes.bool.isRequired,
      relevance: PropTypes.number.isRequired,
      commentStars: PropTypes.number,
      reviewAliasId: PropTypes.number,
      edited: PropTypes.bool.isRequired,
      isReview: PropTypes.bool.isRequired,
      userLevel: PropTypes.number.isRequired,
      userName: PropTypes.string.isRequired,
      userPhoto: PropTypes.string.isRequired,
      userSpecialtyName: PropTypes.string,
      userTypeName: PropTypes.string.isRequired,
      userUsername: PropTypes.string.isRequired,
      offerPhoto: PropTypes.string,
      replies: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    })
  ).isRequired,
  storeDomain: PropTypes.string.isRequired,
  storeImage: PropTypes.string,
  storeName: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
  userName: PropTypes.string.isRequired,
  userSpecialtyName: PropTypes.string,
  userUsername: PropTypes.string.isRequired,
};

export default memo(Main);
